/**
 * Button — primary interactive primitive.
 *
 * Design system mapping (mch-button family):
 *
 *   variant     light bg / text                      dark bg / text                          on hover
 *   primary     webex-gray-950 / white            white/95 / webex-gray-1000               -10 alpha
 *   secondary   transparent + 1px border             transparent + 1px border                bg-fg/8
 *   tertiary    transparent / fg                     transparent / fg                        bg-fg/8
 *   accent      webex-blue-600 / white                webex-blue-600 / white                   webex-blue-700
 *   cancel      webex-red-600 / white                 webex-red-600 / white                    webex-red-700
 *   join        webex-green-600 / white               webex-green-600 / white                  webex-green-700
 *   text        transparent / webex-blue-700          transparent / webex-blue-400             underline
 *
 *   size        compact   default     large       circle
 *               h-6,px-2  h-8,px-3    h-10,px-4   size-8 p-0
 *               text-xs   text-sm     text-base
 *
 * Accessibility:
 *   • Built on Base UI <Button> for proper ARIA + disabled handling.
 *   • Focus ring: ring-2 webex-blue-600 with theme-aware offset.
 *   • Loading: replaces leading icon with spinner; preserves label so SR
 *     gets context. aria-busy set when loading.
 *   • Use `render` prop (Base UI convention) to compose with <a>, <Link>, etc.
 */
import * as React from "react"
import { Button as BaseButton } from "@base-ui-components/react/button"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@lib/cn"

export const buttonVariants = cva(
  [
    "relative inline-flex items-center justify-center gap-1.5 select-none whitespace-nowrap",
    "font-medium leading-none rounded-full border border-transparent",
    "transition-colors duration-150 motion-reduce:transition-none",
    "cursor-pointer",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-webex-blue-600",
    "focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-webex-gray-950",
    "disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none",
    "data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed data-[disabled]:pointer-events-none",
  ].join(" "),
  {
    variants: {
      variant: {
        primary: [
          "bg-webex-gray-950 text-white",
          "hover:bg-webex-gray-800 active:bg-webex-gray-700",
          "dark:bg-webex-gray-50 dark:text-webex-gray-1000",
          "dark:hover:bg-webex-gray-200 dark:active:bg-webex-gray-300",
        ].join(" "),
        secondary: [
          "bg-transparent text-webex-gray-950 border-webex-gray-500",
          "hover:bg-webex-gray-100 active:bg-webex-gray-200",
          "dark:text-webex-gray-50 dark:border-webex-gray-400",
          "dark:hover:bg-webex-gray-850 dark:active:bg-webex-gray-800",
        ].join(" "),
        tertiary: [
          "bg-transparent text-webex-gray-950",
          "hover:bg-webex-gray-100 active:bg-webex-gray-200",
          "dark:text-webex-gray-50",
          "dark:hover:bg-webex-gray-850 dark:active:bg-webex-gray-800",
        ].join(" "),
        accent: [
          "bg-webex-blue-600 text-white",
          "hover:bg-webex-blue-700 active:bg-webex-blue-800",
        ].join(" "),
        cancel: [
          "bg-webex-red-600 text-white",
          "hover:bg-webex-red-700 active:bg-webex-red-800",
        ].join(" "),
        join: [
          "bg-webex-green-600 text-white",
          "hover:bg-webex-green-700 active:bg-webex-green-800",
        ].join(" "),
        text: [
          "bg-transparent rounded px-1",
          "text-webex-blue-700 hover:text-webex-blue-900 hover:underline",
          "dark:text-webex-blue-400 dark:hover:text-webex-blue-200",
        ].join(" "),
      },
      size: {
        compact: "min-h-6 h-6 px-2 text-xs gap-1",
        default: "min-h-8 h-8 px-3 text-sm",
        large: "min-h-10 h-10 px-4 text-base",
        circle: "size-8 p-0 min-h-0",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
      fullWidth: false,
    },
  },
)

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    VariantProps<typeof buttonVariants> {
  loading?: boolean
  leadingIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
  focusableWhenDisabled?: boolean
  /**
   * Replace the rendered element. Pass a React element (e.g. <Link>) to
   * compose Button styles with another component. Equivalent to shadcn's
   * `asChild`, following Base UI's `render` convention.
   */
  render?: React.ReactElement
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      loading = false,
      disabled,
      leadingIcon,
      trailingIcon,
      children,
      render,
      ...props
    },
    ref,
  ) => {
    const merged = cn(buttonVariants({ variant, size, fullWidth }), className)
    const dotSize =
      size === "compact" ? "size-1.5" : size === "large" ? "size-2.5" : "size-2"
    const content = loading ? (
      <>
        {/* Keep label + icons in the layout so width/height don't shift. */}
        <span className="invisible inline-flex items-center gap-1.5" aria-hidden="true">
          {leadingIcon}
          {children}
          {trailingIcon}
        </span>
        <span
          aria-hidden="true"
          className="absolute inset-0 inline-flex items-center justify-center gap-1.5"
        >
          <span className={cn("rounded-full bg-current animate-flash-dot motion-reduce:animate-none", dotSize)} />
          <span className={cn("rounded-full bg-current animate-flash-dot motion-reduce:animate-none [animation-delay:0.2s]", dotSize)} />
          <span className={cn("rounded-full bg-current animate-flash-dot motion-reduce:animate-none [animation-delay:0.4s]", dotSize)} />
        </span>
      </>
    ) : (
      <>
        {leadingIcon}
        {children}
        {trailingIcon}
      </>
    )

    return (
      <BaseButton
        ref={ref}
        className={merged}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...(render ? { render: render as React.ReactElement<Record<string, unknown>> } : {})}
        {...props}
      >
        {content}
      </BaseButton>
    )
  },
)
Button.displayName = "Button"
